import { maskitoWithPlaceholder } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import { TextField } from '@servicetitan/anvil2';
import { Controller, get, useFormContext } from 'react-hook-form';

interface SSNInputProps {
    /**
     * Optional custom mask for SSN.
     * Default is US format: "999-99-9999"
     */
    mask?: string;
    /**
     * Field name for React Hook Form
     */
    name: string;
    /**
     * Label for the input
     */
    label?: string;
    required?: boolean;
}

// Formats a SSN string to the format "XXX-XX-XXXX"
const formatSSN = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{2})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return value;
};

const maskitoOptions = {
    ...maskitoWithPlaceholder('___-__-____', false),
    mask: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
};

/**
 * SSN input with mask that integrates with React Hook Form.
 * By default uses US SSN format: XXX-XX-XXXX
 */
export const SSNInput = ({ name, label, required }: SSNInputProps) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = get(errors, name);

    const maskedInputRef = useMaskito({ options: maskitoOptions });

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const { value, onChange, ref, ...rest } = field;

                // If value is all numbers, format it as a SSN
                const formattedValue = /^\d+$/.test(value) ? formatSSN(value) : value;

                return (
                    <TextField
                        ref={element => {
                            ref(element);
                            maskedInputRef(element);
                        }}
                        placeholder="___-__-____"
                        value={formattedValue}
                        onChange={onChange}
                        required={required}
                        label={label}
                        error={error != null ? error.message : undefined}
                        inputMode="numeric"
                        {...rest}
                    />
                );
            }}
        />
    );
};
